import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  CircularProgress, Alert, Drawer, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip
} from "@mui/material";
import { getAllUserTransactions, payAllPendingTransactions } from "../../services/UserTransaction/service";
import { TransactionStatusEnum } from "../../types/enums";
import { EnrichedSession } from "../../types/models/userSession";
import { fetchAllSessions } from "../../services/UserSession/service";
import { formatLocalDate } from "../../utils/time";

const isForceClosed = (end_time: string | null): boolean => end_time === "1969-12-31T23:59:59";

const getSessionStatus = (session: EnrichedSession): string => {
  if (session.end_time === null) return "Active";
  if (isForceClosed(session.end_time)) return "Error";
  return "Ended";
};

const Billing: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [allSessions, setAllSessions] = useState<EnrichedSession[]>([]);
  const [selectedUser, setSelectedUser] = useState<EnrichedSession | null>(null);
  const [sortBy, setSortBy] = useState<keyof EnrichedSession>("start_time");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionGroups, userBilling] = await Promise.all([
          fetchAllSessions(),
          getAllUserTransactions(),
        ]);

        let sessions: EnrichedSession[] = [];

        sessionGroups.forEach((group) => {
          const user = userBilling.find((u) => u.user_id === group.user_id);
          const ungroupedTransactions = user?.transactions.filter((txn) => txn.session_id === null) ?? [];

          const realSessions = group.sessions.map((session) => {
            const transactions = user?.transactions.filter((txn) => txn.session_id === session.id) ?? [];

            const debt = transactions.filter((txn) =>
              txn.status === TransactionStatusEnum.AWAITING_CONFIRMATION || txn.status === TransactionStatusEnum.INITIATED).length;

            const lastVisit = transactions.reduce((latest, txn) => {
              const created = txn.created_timestamp ? new Date(txn.created_timestamp).getTime() : 0;
              const latestTime = new Date(latest).getTime();
              return created > latestTime ? txn.created_timestamp! : latest;
            }, "1970-01-01T00:00:00Z");

            return {
              id: session.id,
              user_id: user?.user_id || -1,
              username: user?.username || "Unknown",
              email: user?.email,
              phone: user?.phone,
              serial: session.serial,
              start_time: session.start_time,
              end_time: session.end_time,
              last_ping: session.last_ping,
              transactions,
              usage: transactions.length,
              debt,
              lastVisit,
            };
          });

          sessions.push(...realSessions);

          if (ungroupedTransactions.length > 0 && user) {
            const lastVisit = ungroupedTransactions.reduce((latest, txn) => {
              const created = txn.created_timestamp ? new Date(txn.created_timestamp).getTime() : 0;
              const latestTime = new Date(latest).getTime();
              return created > latestTime ? txn.created_timestamp! : latest;
            }, "1970-01-01T00:00:00Z");

            sessions.push({
              id: -1 * user.user_id,
              user_id: user.user_id,
              username: user.username || "Unknown",
              email: user.email,
              phone: user.phone,
              serial: "Ungrouped",
              start_time: lastVisit,
              end_time: lastVisit,
              last_ping: null,
              transactions: ungroupedTransactions,
              usage: ungroupedTransactions.length,
              debt: ungroupedTransactions.filter((txn) =>
                txn.status === TransactionStatusEnum.AWAITING_CONFIRMATION || txn.status === TransactionStatusEnum.INITIATED).length,
              lastVisit,
            });
          }
        });

        setAllSessions(sessions.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()));
      } catch (error) {
        setError("Failed to fetch sessions or transactions.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSort = (field: keyof EnrichedSession) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  const handleUserClick = (user: EnrichedSession) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  const handlePay = async () => {
    if (selectedUser) {
      try {
        const transactionIds = selectedUser.transactions
          .filter((txn) => txn.status === TransactionStatusEnum.AWAITING_CONFIRMATION || txn.status === TransactionStatusEnum.INITIATED)
          .map((txn) => txn.id);

        await payAllPendingTransactions(selectedUser.user_id, transactionIds);
        setDrawerOpen(false);
        window.location.reload();
      } catch (error) {
        console.error("Error updating transactions:", error);
      }
    }
  };

  const headerCellStyle = {
    color: "#ffffff",
    fontWeight: "bold",
    cursor: "pointer"
  };

  const filteredSessions = allSessions
    .filter((session) => (showActiveOnly ? session.end_time === null : true))
    .filter((session) => (showUnpaidOnly ? session.debt > 0 : true));

  const sortedSessions = [...filteredSessions].sort((a, b) => {
    const isAActive = a.end_time === null;
    const isBActive = b.end_time === null;
    if (isAActive && !isBActive) return -1;
    if (!isAActive && isBActive) return 1;

    const aVal = a[sortBy] ?? "";
    const bVal = b[sortBy] ?? "";

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }

    return sortDirection === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);
  const confirmPayment = () => {
    handlePay();
    closeDialog();
  };
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 4 }, backgroundColor: "rgba(14, 23, 32, 0)", minHeight: "100vh", width: "100%" }}>
      <Typography variant="h4" gutterBottom sx={{ color: "#FFFFFF", fontWeight: "bold" }}>
        Usage
      </Typography>

      <Paper sx={{ borderRadius: "8px", backgroundColor: "rgba(14, 23, 32, 0)", }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 600 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#071019" }}>
                <TableCell sx={{ ...headerCellStyle, borderTopLeftRadius: "5px", borderBottomLeftRadius: "5px", borderTop: "1px solid #394047", borderLeft: "1px solid #394047", borderBottom: "1px solid #394047" }}>Status</TableCell>
                <TableCell sx={{ ...headerCellStyle, borderTop: "1px solid #394047", borderBottom: "1px solid #394047" }} onClick={() => handleSort("username")}>User</TableCell>
                <TableCell sx={{ ...headerCellStyle, borderTop: "1px solid #394047", borderBottom: "1px solid #394047" }}>Session Time</TableCell>
                <TableCell sx={{ ...headerCellStyle, borderTop: "1px solid #394047", borderBottom: "1px solid #394047" }}>Training Time</TableCell>
                <TableCell sx={{ ...headerCellStyle, borderTopRightRadius: "5px", borderBottomRightRadius: "5px", borderTop: "1px solid #394047", borderRight: "1px solid #394047", borderBottom: "1px solid #394047" }} onClick={() => handleSort("start_time")}>Start Time</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedSessions.map((session, index) => {
                const getLoginTime = () => {
                  const start = new Date(formatLocalDate(session.start_time)).getTime();
                  const end = session.end_time ? new Date(session.end_time).getTime() : Date.now();
                  const diff = end - start;
                  if (diff <= 0) return "-";
                  const minutes = Math.floor(diff / 60000) % 60;
                  const hours = Math.floor(diff / (1000 * 60 * 60));
                  return `${hours}h ${minutes}m`;
                };

                const getTotalTrainingTime = () => {
                  const totalMillis = session.transactions.reduce((acc, txn) => {
                    if (txn.created_timestamp && txn.finished_timestamp) {
                      const start = new Date(txn.created_timestamp).getTime();
                      const end = new Date(txn.finished_timestamp).getTime();
                      return acc + (end - start);
                    }
                    return acc;
                  }, 0);

                  if (totalMillis === 0) return "-";

                  const totalMinutes = Math.floor(totalMillis / 60000);
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;

                  if (hours === 0) return `${minutes}m`;
                  if (minutes === 0) return `${hours}h`;
                  return `${hours}h ${minutes}m`;
                };

                return (
                  <TableRow key={session.id} hover onClick={() => handleUserClick(session)} sx={{
                    cursor: "pointer",
                    backgroundColor: index % 2 === 0 ? "none" : "none",
                    "&:hover": { backgroundColor: "#23303F" },
                    borderBottom: "none",
                  }}>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #262E36" }}>
                      {(() => {
                        if (session.serial === "Ungrouped") {
                          return <Chip label="Ungrouped" size="small" sx={{ backgroundColor: "#7C3AED", color: "#FFFFFF" }} />;
                        }

                        const status = getSessionStatus(session);

                        if (status === "Active") return <Chip label="Active" size="small" sx={{ backgroundColor: "#103F21", color: "#17E324" }} />;
                        if (status === "Ended") return <Chip label="Ended" size="small" sx={{ backgroundColor: "#3E454D", color: "#A0A4A8" }} />;
                        if (status === "Error") return <Chip label="Error" size="small" sx={{ backgroundColor: "#5B1F1F", color: "#FF7676" }} />;

                        return status;
                      })()}
                    </TableCell>

                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #262E36" }}>{session.username}</TableCell>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #262E36" }}>{getLoginTime()}</TableCell>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #262E36" }}>{getTotalTrainingTime()}</TableCell>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #262E36" }}>
                      {session.start_time ? formatLocalDate(session.start_time, "MMMM d, yyyy | HH:mm") : "N/A"}

                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>




      {/* Drawer stays on the right */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100vw", sm: 600 }, backgroundColor: "#0E1720", borderLeft: "1px solid #51585E", color: "white", p: 2 }
        }}
      >
        {selectedUser && (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", marginBottom: "10px", marginTop: "10px" }}>Session Info</Typography>
            <Typography>User: {selectedUser.username}</Typography>
            <Typography>Email: {selectedUser.email || "N/A"}</Typography>
            <Typography>Phone: {selectedUser.phone || "N/A"}</Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Session: {selectedUser.serial === "Ungrouped" ? "Ungrouped Transactions" : selectedUser.serial}
            </Typography>
            <Table
              sx={{
                marginTop: 2,
                borderCollapse: "separate",
                borderSpacing: 0,
              }}>
              <TableHead sx={{ backgroundColor: "#071019", fontWeight: "bold", }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", borderTopLeftRadius: "5px", borderBottomLeftRadius: "5px", borderTop: "1px solid #394047", borderLeft: "1px solid #394047", borderBottom: "1px solid #394047", color: "#FFFFFF" }}>Scenario</TableCell>
                  <TableCell sx={{ fontWeight: "bold", borderTop: "1px solid #394047", borderBottom: "1px solid #394047", color: "#FFFFFF" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", borderTopRightRadius: "5px", borderBottomRightRadius: "5px", borderTop: "1px solid #394047", borderBottom: "1px solid #394047", borderRight: "1px solid #394047", color: "#FFFFFF" }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedUser.transactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #394047" }}>{txn.scenario_name}</TableCell>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #394047" }}>
                      {(() => {
                        const status = txn.status ?? "Unknown";
                        if (["PAID", "AWAITING_CONFIRMATION"].includes(status)) return "Completed";
                        if (status === "INITIATED") return "Initiated";
                        return status;
                      })()}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF", borderBottom: "1px solid #394047" }}>
                      {txn.created_timestamp ? formatLocalDate(txn.created_timestamp) : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Drawer>

      <Dialog open={dialogOpen} onClose={closeDialog}>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to pay all unpaid transactions for {selectedUser?.username}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ color: "#FF4B4B" }}>
            Cancel
          </Button>
          <Button onClick={confirmPayment} sx={{ backgroundColor: "#64FFDA", color: "#161B22" }} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
